/**
 * User-facing toast titles and bodies for bond bidding bulk operations.
 * Plain language only — no technical terms (correlation id, server, database, etc.).
 */

export const BOND_BIDDING_TOAST_TITLE = {
  uploadFailed: 'Upload failed',
  uploadWarning: 'Upload warning',
  uploadComplete: 'Upload complete',
  downloadFailed: 'Download failed',
  exportComplete: 'Export Complete',
  exportFailed: 'Export Failed',
  noDataToExport: 'No Data to Export',
  submitAll: 'Submit All',
} as const;

export const BOND_BIDDING_MESSAGES = {
  upload: {
    notCsv: 'File must be a CSV (.csv).',
    parseFailedFallback: 'Failed to parse CSV.',
    emptyCsv: 'CSV is empty or has no data rows.',
    backendControlledResponseType:
      'Response Type Price/Spread/ Yield predefined on records cannot be changed. The response will be applied to the records for the predefined response type.',
    noValidReferenceIds:
      'No valid Reference# values found. Only numeric Reference# values are allowed.',
    someReferenceIdsIgnored:
      'Some Reference# values were skipped because they are not numbers.',
    filterEventFailedFallback:
      'Your upload could not be saved. Please try again.',
    noCorrelationId:
      'Your upload could not be completed. Please try again.',
    registerBatchFailed:
      'Your upload could not be saved. Please try again.',
    validationRetry:
      'Upload could not be completed. Please try again. If this keeps happening, contact support.',
    validationAllFirmUntil: (errorRows: number) =>
      errorRows === 1
        ? '1 row could not be uploaded because the bid time has passed.'
        : `${errorRows} rows could not be uploaded because the bid time has passed.`,
    validationNoValidWithErrors: (errorRows: number, ignoredRows: number) =>
      `No valid rows. ${errorRows} validation error(s), ${ignoredRows} not found in database.`,
    validationNoValidAllIgnored: (ignoredRows: number) =>
      `No valid rows. All ${ignoredRows} reference(s) not found in database.`,
    completePartial: (validRows: number, ignoredRows: number, errorRows: number) =>
      `Upload complete: ${validRows} rows updated, ${ignoredRows} not found, ${errorRows} error(s) skipped.`,
    completeSuccess: (validRows: number) =>
      `Upload complete: ${validRows} rows updated.`,
  },
  download: {
    serverUnavailable:
      'We could not reach the system. Please try again in a moment. If this keeps happening, contact support.',
    exportRejected: 'Download could not be completed. Please try again.',
    noRowsMatchFilters: 'No rows match the current filters.',
    exportFailedFallback: 'Download could not be completed.',
    exportedRows: (count: number) =>
      count === 1 ? '1 row downloaded.' : `${count} rows downloaded.`,
  },
  submitAll: {
    gridNotReady: 'Please wait for the list to finish loading, then try again.',
    needReferenceFilter:
      'Enter or filter by Reference# first, then choose which bonds to submit.',
    noEligibleBids:
      'No bids in your selection can be submitted right now. They may already be submitted or no longer available.',
    requestFailedFallback: 'Submit could not be completed. Please try again.',
    submitted: (count: number) =>
      count === 1 ? '1 bid submitted.' : `${count} bids submitted.`,
    partialFailure: (succeeded: number, failed: number) =>
      `${succeeded} bid(s) submitted. ${failed} could not be submitted — please review those rows.`,
  },
} as const;
